/**
 * applyArMaterials — Colours a model-viewer GLB before entering WebXR.
 *
 * Uses model-viewer's Scene Graph API (model.getMaterialByName,
 * pbrMetallicRoughness.setBaseColorFactor, createTexture, etc.) to apply
 * the user's current exterior color and interior variant materials to the
 * AR GLB. This allows a single "base" GLB to represent all colour
 * configurations in Android WebXR without shipping individual files.
 *
 * Called after model-viewer's `load` event, before `activateAR()`.
 */

// ---------------------------------------------------------------------------
// Types — mirrors model-viewer's Scene Graph API surface we need
// ---------------------------------------------------------------------------

interface MvPBR {
  setBaseColorFactor(rgba: [number, number, number, number] | string): void;
  readonly baseColorTexture: MvTextureInfo | null;
}

interface MvTextureInfo {
  setTexture(tex: MvTexture | null): void;
}

interface MvTexture {
  name: string;
}

interface MvMaterial {
  name: string;
  pbrMetallicRoughness: MvPBR;
}

interface MvModel {
  materials: MvMaterial[];
  getMaterialByName(name: string): MvMaterial | null;
}

/** model-viewer element with scene graph + texture factory. */
export interface MvElement extends HTMLElement {
  model?: MvModel;
  createTexture(uri: string, type?: string): Promise<MvTexture>;
}

// ---------------------------------------------------------------------------
// Public types — describe what to apply
// ---------------------------------------------------------------------------

/** A single material colour change (car paint layer or interior part). */
export interface ArMaterialChange {
  /** GLB material name, e.g. "M_CarPaint_Primary". */
  target: string;
  /** Hex colour code, e.g. "#C0251A". */
  colorCode?: string;
  /** Optional diffuse texture URL to swap (interior atlas textures). */
  diffuseUrl?: string;
}

// ---------------------------------------------------------------------------
// Hex → linear RGBA helper
// ---------------------------------------------------------------------------

function hexToLinearRgba(hex: string): [number, number, number, number] {
  const h = hex.replace('#', '');
  const r = parseInt(h.substring(0, 2), 16) / 255;
  const g = parseInt(h.substring(2, 4), 16) / 255;
  const b = parseInt(h.substring(4, 6), 16) / 255;
  // sRGB → linear
  const toLinear = (c: number) => (c <= 0.04045 ? c / 12.92 : ((c + 0.055) / 1.055) ** 2.4);
  return [toLinear(r), toLinear(g), toLinear(b), 1.0];
}

// ---------------------------------------------------------------------------
// Main function
// ---------------------------------------------------------------------------

/**
 * Applies exterior colour + optional interior variant materials to a
 * model-viewer element's loaded GLB.
 *
 * @param mv  The <model-viewer> DOM element (must have `model` populated).
 * @param changes  Array of material changes to apply.
 * @returns Promise that resolves when all textures have been swapped.
 *
 * Skips silently when:
 * - `mv.model` is not yet available
 * - A material name is not found in the GLB (common when AR GLB has
 *   fewer meshes than the main model)
 */
export async function applyArMaterials(mv: MvElement, changes: ArMaterialChange[]): Promise<void> {
  const model = mv.model;
  if (!model) return;

  for (const change of changes) {
    const mat = model.getMaterialByName(change.target);
    if (!mat) continue;

    // Apply colour
    if (change.colorCode) {
      mat.pbrMetallicRoughness.setBaseColorFactor(hexToLinearRgba(change.colorCode));
    }

    // Apply diffuse texture
    if (change.diffuseUrl) {
      const tex = await mv.createTexture(change.diffuseUrl);
      mat.pbrMetallicRoughness.baseColorTexture?.setTexture(tex);
    }
  }
}

// ---------------------------------------------------------------------------
// Convenience: build ArMaterialChange[] from resolved config state
// ---------------------------------------------------------------------------

import type { ColorDefinition, InteriorVariant } from '../types/config';

/**
 * Builds the list of material changes from the user's current selections.
 *
 * @param color           The full ColorDefinition for the selected exterior colour.
 * @param interiorVariant The matching InteriorVariant for the selected pano (if any).
 */
export function buildArMaterialChanges(
  color: ColorDefinition | undefined,
  interiorVariant: InteriorVariant | undefined,
): ArMaterialChange[] {
  const changes: ArMaterialChange[] = [];

  // Exterior paint — primary + optional secondary (two-tone)
  if (color) {
    for (const matName of color.primary.materials ?? []) {
      changes.push({ target: matName, colorCode: color.primary.colorCode });
    }
    if (color.secondary) {
      for (const matName of color.secondary.materials ?? []) {
        changes.push({ target: matName, colorCode: color.secondary.colorCode });
      }
    }
  }

  // Interior variant materials
  if (interiorVariant) {
    for (const m of interiorVariant.materials) {
      const targets = Array.isArray(m.target) ? m.target : [m.target];
      for (const target of targets) {
        changes.push({ target, colorCode: m.colorCode, diffuseUrl: m.diffuseUrl });
      }
    }
  }

  return changes;
}
